import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { MapPinIcon, StarIcon, WifiIcon, CarIcon, UtensilsIcon, DumbbellIcon, SparklesIcon, ShieldCheckIcon, ConciergeBellIcon, GlassWaterIcon, TreesIcon, BedDoubleIcon, MaximizeIcon, UsersIcon, CheckIcon, XIcon, CalendarIcon, UserIcon, MailIcon, PhoneIcon, MessageSquareIcon, ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon, AwardIcon, CoffeeIcon, BathIcon } from 'lucide-react';
interface HotelPageProps {
  onNavigate: (page: string) => void;
}
/* ═══════════════════════════════════════════
   IMAGE GALLERY GRID
   ═══════════════════════════════════════════ */
function ImageGallery() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const images = [{
    src: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80',
    alt: "Dwarika's Hotel main courtyard with traditional Newari architecture"
  }, {
    src: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80',
    alt: "Dwarika's Hotel luxury suite interior"
  }, {
    src: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80',
    alt: "Dwarika's Hotel swimming pool and garden"
  }, {
    src: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80',
    alt: "Dwarika's Hotel restaurant and dining area"
  }, {
    src: 'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=800&q=80',
    alt: "Dwarika's Hotel spa and wellness center"
  }];
  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  return <>
      <section className="w-full" data-id="element-1369">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1370">
          <div className="grid grid-cols-4 grid-rows-2 gap-2 sm:gap-3 h-[300px] sm:h-[420px] lg:h-[500px] rounded-2xl overflow-hidden" data-id="element-1371">
            {/* Main large image */}
            <button onClick={() => openLightbox(0)} className="col-span-2 row-span-2 relative group cursor-pointer" data-id="element-1372">
              <img src={images[0].src} alt={images[0].alt} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" data-id="element-1373" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" data-id="element-1374" />
            </button>

            {/* Top right images */}
            <button onClick={() => openLightbox(1)} className="relative group cursor-pointer" data-id="element-1375">
              <img src={images[1].src} alt={images[1].alt} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" data-id="element-1376" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" data-id="element-1377" />
            </button>
            <button onClick={() => openLightbox(2)} className="relative group cursor-pointer" data-id="element-1378">
              <img src={images[2].src} alt={images[2].alt} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" data-id="element-1379" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" data-id="element-1380" />
            </button>

            {/* Bottom right images */}
            <button onClick={() => openLightbox(3)} className="relative group cursor-pointer" data-id="element-1381">
              <img src={images[3].src} alt={images[3].alt} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" data-id="element-1382" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" data-id="element-1383" />
            </button>
            <button onClick={() => openLightbox(4)} className="relative group cursor-pointer" data-id="element-1384">
              <img src={images[4].src} alt={images[4].alt} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" data-id="element-1385" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" data-id="element-1386" />
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-1387">
                <span className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-slate-800" data-id="element-1388">
                  View All Photos
                </span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence data-id="element-1389">
        {lightboxOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={() => setLightboxOpen(false)} data-id="element-1390">
            <button onClick={() => setLightboxOpen(false)} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" aria-label="Close lightbox" data-id="element-1391">
              <XIcon className="w-5 h-5" data-id="element-1392" />
            </button>
            <button onClick={e => {
          e.stopPropagation();
          setLightboxIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
        }} className="absolute left-4 sm:left-8 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" aria-label="Previous image" data-id="element-1393">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-1394" />
            </button>
            <motion.img key={lightboxIndex} initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.3
        }} src={images[lightboxIndex].src} alt={images[lightboxIndex].alt} className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} data-id="element-1395" />
            <button onClick={e => {
          e.stopPropagation();
          setLightboxIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
        }} className="absolute right-4 sm:right-8 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" aria-label="Next image" data-id="element-1396">
              <ChevronRightIcon className="w-5 h-5" data-id="element-1397" />
            </button>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2" data-id="element-1398">
              {images.map((_, i) => <button key={i} onClick={e => {
            e.stopPropagation();
            setLightboxIndex(i);
          }} className={`w-2 h-2 rounded-full transition-all ${i === lightboxIndex ? 'bg-white w-6' : 'bg-white/40'}`} aria-label={`Go to image ${i + 1}`} data-id="element-1399" />)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
/* ═══════════════════════════════════════════
   HOTEL INFO HEADER
   ═══════════════════════════════════════════ */
function HotelInfo({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  return <section className="w-full bg-white pt-28 pb-10 sm:pb-14" ref={ref} data-id="element-1400">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1401">
        {/* Breadcrumb */}
        <nav className="mb-6" aria-label="Breadcrumb" data-id="element-1402">
          <ol className="flex items-center gap-2 text-sm text-slate-400" data-id="element-1403">
            <li data-id="element-1404">
              <button onClick={() => onNavigate('home')} className="hover:text-slate-700 transition-colors" data-id="element-1405">
                Home
              </button>
            </li>
            <li data-id="element-1406">/</li>
            <li data-id="element-1407">
              <button onClick={() => onNavigate('home')} className="hover:text-slate-700 transition-colors" data-id="element-1408">
                Hotels
              </button>
            </li>
            <li data-id="element-1409">/</li>
            <li className="text-[#D4A853]" data-id="element-1410">Dwarika's Hotel</li>
          </ol>
        </nav>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} data-id="element-1411">
          {/* Category Badge */}
          <div className="flex flex-wrap items-center gap-3 mb-3" data-id="element-1412">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#D4A853]/10 text-[#D4A853] text-xs font-semibold rounded-full border border-[#D4A853]/20" data-id="element-1413">
              <AwardIcon className="w-3.5 h-3.5" data-id="element-1414" />
              Heritage Luxury
            </span>
            <div className="flex items-center gap-0.5" data-id="element-1415">
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-1416" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-1417" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-1418" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-1419" />
              <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-1420" />
            </div>
          </div>

          {/* Hotel Name */}
          <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3" data-id="element-1421">
            Dwarika's Hotel
          </h1>

          {/* Location & Reviews */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-id="element-1422">
            <div className="flex items-center gap-1.5 text-slate-500" data-id="element-1423">
              <MapPinIcon className="w-4 h-4 text-teal-600" data-id="element-1424" />
              <span className="text-sm" data-id="element-1425">Battisputali, Kathmandu, Nepal</span>
            </div>

            {/* TripAdvisor Reviews */}
            <div className="flex items-center gap-2" data-id="element-1426">
              <div className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 rounded-lg border border-emerald-100" data-id="element-1427">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-emerald-600" fill="currentColor" data-id="element-1428">
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-1429" />
                  <circle cx="12" cy="12" r="4" data-id="element-1430" />
                </svg>
                <span className="text-sm font-semibold text-emerald-700" data-id="element-1431">
                  4.8
                </span>
              </div>
              <span className="text-sm text-slate-500" data-id="element-1432">
                1,247 reviews on TripAdvisor
              </span>
              <a href="#" className="text-sm text-teal-600 hover:text-teal-700 transition-colors flex items-center gap-1" data-id="element-1433">
                Read reviews
                <ExternalLinkIcon className="w-3 h-3" data-id="element-1434" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   HOTEL ABOUT / DESCRIPTION
   ═══════════════════════════════════════════ */
function HotelAbout() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-1435">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1436">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} data-id="element-1437">
          <div className="flex items-center gap-3 mb-6" data-id="element-1438">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1439">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1440" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1441">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-6" data-id="element-1442">
            About the Hotel
          </h2>
          <div className="space-y-4 text-slate-600 leading-relaxed text-[15px]" data-id="element-1443">
            <p data-id="element-1444">
              Dwarika's Hotel is a living museum of Nepali art and architecture,
              widely regarded as one of Asia's finest heritage hotels. Founded
              by the late Dwarika Das Shrestha, this extraordinary property
              preserves centuries-old Newari woodcarvings, terracotta artworks,
              and traditional craftsmanship that might otherwise have been lost
              to time.
            </p>
            <p data-id="element-1445">
              Located in the heart of Kathmandu, the hotel seamlessly blends
              ancient Nepali heritage with modern luxury. Every corridor,
              courtyard, and room tells a story — from the intricately carved
              wooden window frames salvaged from demolished medieval buildings
              to the hand-laid brick courtyards that echo the grandeur of
              Nepal's royal palaces.
            </p>
            <p data-id="element-1446">
              The hotel features 87 exquisitely appointed rooms and suites, each
              uniquely designed with traditional Newari elements. Guests enjoy
              world-class dining at Krishnarpan — Nepal's only restaurant
              offering a six-to-twenty-two course Nepali degustation menu —
              along with an award-winning spa, tranquil gardens, and the
              legendary Dwarika's hospitality that has earned it recognition
              from UNESCO and countless international travel awards.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10" data-id="element-1447">
            <div className="bg-white rounded-xl p-5 border border-[#D4A853]/10 text-center" data-id="element-1448">
              <p className="font-playfair text-2xl font-bold text-teal-600" data-id="element-1449">
                87
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1450">Rooms & Suites</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-[#D4A853]/10 text-center" data-id="element-1451">
              <p className="font-playfair text-2xl font-bold text-teal-600" data-id="element-1452">
                4
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1453">Restaurants & Bars</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-[#D4A853]/10 text-center" data-id="element-1454">
              <p className="font-playfair text-2xl font-bold text-teal-600" data-id="element-1455">
                1977
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1456">Year Established</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-[#D4A853]/10 text-center" data-id="element-1457">
              <p className="font-playfair text-2xl font-bold text-teal-600" data-id="element-1458">
                15+
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1459">Awards Won</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   ROOM CATEGORIES
   ═══════════════════════════════════════════ */
function RoomCategories({
  onSelectRoom
}: {
  onSelectRoom: (room: string) => void;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-white py-20 sm:py-28" ref={ref} data-id="element-1460">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1461">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mb-12" data-id="element-1462">
          <div className="flex items-center gap-3 mb-6" data-id="element-1463">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1464">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1465" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1466">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-1467">
            Rooms & Suites
          </h2>
          <p className="text-slate-500 font-light text-lg" data-id="element-1468">
            Choose your perfect retreat in the heart of Kathmandu
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1469">
          {/* Superior Room */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0
        }} className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm" data-id="element-1470">
            <div className="relative aspect-[4/3] overflow-hidden" data-id="element-1471">
              <img src="https://images.unsplash.com/photo-1590490360182-c33d57733427?w=600&q=80" alt="Superior Room at Dwarika's Hotel with traditional Newari decor" className="w-full h-full object-cover" data-id="element-1472" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm text-slate-800 text-xs font-semibold rounded-full" data-id="element-1473">
                Superior
              </span>
            </div>
            <div className="p-6" data-id="element-1474">
              <h3 className="font-playfair text-xl font-semibold text-slate-900 mb-3" data-id="element-1475">
                Superior Heritage Room
              </h3>
              <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-slate-500" data-id="element-1476">
                <span className="flex items-center gap-1.5" data-id="element-1477">
                  <MaximizeIcon className="w-4 h-4 text-[#D4A853]" data-id="element-1478" />
                  32 m²
                </span>
                <span className="flex items-center gap-1.5" data-id="element-1479">
                  <BedDoubleIcon className="w-4 h-4 text-[#D4A853]" data-id="element-1480" />
                  King Bed
                </span>
                <span className="flex items-center gap-1.5" data-id="element-1481">
                  <UsersIcon className="w-4 h-4 text-[#D4A853]" data-id="element-1482" />2 Guests
                </span>
              </div>
              <ul className="space-y-1.5 mb-5" data-id="element-1483">
                <li className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1484">
                  <CheckIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" data-id="element-1485" />
                  Garden or courtyard view
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1486">
                  <CheckIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" data-id="element-1487" />
                  Traditional Newari woodwork
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1488">
                  <CheckIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" data-id="element-1489" />
                  Complimentary breakfast
                </li>
              </ul>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-1490">
                <div data-id="element-1491">
                  <span className="text-teal-600 text-xl font-semibold" data-id="element-1492">
                    $250
                  </span>
                  <span className="text-slate-400 text-sm" data-id="element-1493"> / night</span>
                </div>
                <button onClick={() => onSelectRoom('Superior Heritage Room')} className="px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors shadow-lg shadow-teal-600/20" data-id="element-1494">
                  Check Availability
                </button>
              </div>
            </div>
          </motion.div>

          {/* Deluxe Room */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.12
        }} className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm ring-2 ring-[#D4A853]/30" data-id="element-1495">
            <div className="relative aspect-[4/3] overflow-hidden" data-id="element-1496">
              <img src="https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&q=80" alt="Deluxe Room at Dwarika's Hotel with luxury furnishings" className="w-full h-full object-cover" data-id="element-1497" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-1498">
                Most Popular
              </span>
            </div>
            <div className="p-6" data-id="element-1499">
              <h3 className="font-playfair text-xl font-semibold text-slate-900 mb-3" data-id="element-1500">
                Deluxe Heritage Room
              </h3>
              <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-slate-500" data-id="element-1501">
                <span className="flex items-center gap-1.5" data-id="element-1502">
                  <MaximizeIcon className="w-4 h-4 text-[#D4A853]" data-id="element-1503" />
                  45 m²
                </span>
                <span className="flex items-center gap-1.5" data-id="element-1504">
                  <BedDoubleIcon className="w-4 h-4 text-[#D4A853]" data-id="element-1505" />
                  King Bed
                </span>
                <span className="flex items-center gap-1.5" data-id="element-1506">
                  <UsersIcon className="w-4 h-4 text-[#D4A853]" data-id="element-1507" />2 Guests
                </span>
              </div>
              <ul className="space-y-1.5 mb-5" data-id="element-1508">
                <li className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1509">
                  <CheckIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" data-id="element-1510" />
                  Private balcony with garden view
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1511">
                  <CheckIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" data-id="element-1512" />
                  Handcrafted copper bathtub
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1513">
                  <CheckIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" data-id="element-1514" />
                  Breakfast & evening cocktail
                </li>
              </ul>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-1515">
                <div data-id="element-1516">
                  <span className="text-teal-600 text-xl font-semibold" data-id="element-1517">
                    $350
                  </span>
                  <span className="text-slate-400 text-sm" data-id="element-1518"> / night</span>
                </div>
                <button onClick={() => onSelectRoom('Deluxe Heritage Room')} className="px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors shadow-lg shadow-teal-600/20" data-id="element-1519">
                  Check Availability
                </button>
              </div>
            </div>
          </motion.div>

          {/* Royal Suite */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.24
        }} className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm" data-id="element-1520">
            <div className="relative aspect-[4/3] overflow-hidden" data-id="element-1521">
              <img src="https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=600&q=80" alt="Royal Suite at Dwarika's Hotel with panoramic views" className="w-full h-full object-cover" data-id="element-1522" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm text-slate-800 text-xs font-semibold rounded-full" data-id="element-1523">
                Suite
              </span>
            </div>
            <div className="p-6" data-id="element-1524">
              <h3 className="font-playfair text-xl font-semibold text-slate-900 mb-3" data-id="element-1525">
                Royal Dwarika Suite
              </h3>
              <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-slate-500" data-id="element-1526">
                <span className="flex items-center gap-1.5" data-id="element-1527">
                  <MaximizeIcon className="w-4 h-4 text-[#D4A853]" data-id="element-1528" />
                  85 m²
                </span>
                <span className="flex items-center gap-1.5" data-id="element-1529">
                  <BedDoubleIcon className="w-4 h-4 text-[#D4A853]" data-id="element-1530" />
                  King Bed
                </span>
                <span className="flex items-center gap-1.5" data-id="element-1531">
                  <UsersIcon className="w-4 h-4 text-[#D4A853]" data-id="element-1532" />3 Guests
                </span>
              </div>
              <ul className="space-y-1.5 mb-5" data-id="element-1533">
                <li className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1534">
                  <CheckIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" data-id="element-1535" />
                  Separate living room & study
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1536">
                  <CheckIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" data-id="element-1537" />
                  Private terrace with mountain view
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1538">
                  <CheckIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" data-id="element-1539" />
                  Butler service & airport transfer
                </li>
              </ul>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-1540">
                <div data-id="element-1541">
                  <span className="text-teal-600 text-xl font-semibold" data-id="element-1542">
                    $650
                  </span>
                  <span className="text-slate-400 text-sm" data-id="element-1543"> / night</span>
                </div>
                <button onClick={() => onSelectRoom('Royal Dwarika Suite')} className="px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors shadow-lg shadow-teal-600/20" data-id="element-1544">
                  Check Availability
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   HOTEL FACILITIES
   ═══════════════════════════════════════════ */
function HotelFacilities() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-1545">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1546">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mb-12" data-id="element-1547">
          <div className="flex items-center gap-3 mb-6" data-id="element-1548">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1549">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1550" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1551">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-1552">
            Hotel Facilities
          </h2>
          <p className="text-slate-500 font-light text-lg" data-id="element-1553">
            Everything you need for an exceptional stay
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-1554">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0
        }} className="bg-white rounded-xl p-5 border border-[#D4A853]/10 flex items-start gap-4" data-id="element-1555">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1556">
              <SparklesIcon className="w-5 h-5 text-teal-600" data-id="element-1557" />
            </div>
            <div data-id="element-1558">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-1559">
                Pancha Kosha Spa
              </p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-1560">
                Award-winning wellness
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.05
        }} className="bg-white rounded-xl p-5 border border-[#D4A853]/10 flex items-start gap-4" data-id="element-1561">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1562">
              <UtensilsIcon className="w-5 h-5 text-teal-600" data-id="element-1563" />
            </div>
            <div data-id="element-1564">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-1565">
                Krishnarpan Restaurant
              </p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-1566">
                Fine Nepali degustation
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="bg-white rounded-xl p-5 border border-[#D4A853]/10 flex items-start gap-4" data-id="element-1567">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1568">
              <GlassWaterIcon className="w-5 h-5 text-teal-600" data-id="element-1569" />
            </div>
            <div data-id="element-1570">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-1571">
                Swimming Pool
              </p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-1572">
                Heated outdoor pool
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.15
        }} className="bg-white rounded-xl p-5 border border-[#D4A853]/10 flex items-start gap-4" data-id="element-1573">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1574">
              <DumbbellIcon className="w-5 h-5 text-teal-600" data-id="element-1575" />
            </div>
            <div data-id="element-1576">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-1577">
                Fitness Center
              </p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-1578">Modern equipment</p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.2
        }} className="bg-white rounded-xl p-5 border border-[#D4A853]/10 flex items-start gap-4" data-id="element-1579">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1580">
              <WifiIcon className="w-5 h-5 text-teal-600" data-id="element-1581" />
            </div>
            <div data-id="element-1582">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-1583">Free Wi-Fi</p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-1584">
                High-speed throughout
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.25
        }} className="bg-white rounded-xl p-5 border border-[#D4A853]/10 flex items-start gap-4" data-id="element-1585">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1586">
              <CarIcon className="w-5 h-5 text-teal-600" data-id="element-1587" />
            </div>
            <div data-id="element-1588">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-1589">
                Airport Transfer
              </p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-1590">
                Complimentary pickup
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.3
        }} className="bg-white rounded-xl p-5 border border-[#D4A853]/10 flex items-start gap-4" data-id="element-1591">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1592">
              <TreesIcon className="w-5 h-5 text-teal-600" data-id="element-1593" />
            </div>
            <div data-id="element-1594">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-1595">
                Heritage Gardens
              </p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-1596">
                Tranquil courtyards
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.35
        }} className="bg-white rounded-xl p-5 border border-[#D4A853]/10 flex items-start gap-4" data-id="element-1597">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1598">
              <ShieldCheckIcon className="w-5 h-5 text-teal-600" data-id="element-1599" />
            </div>
            <div data-id="element-1600">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-1601">
                24/7 Security
              </p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-1602">
                Safe & secure premises
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   BOOKING FORM
   ═══════════════════════════════════════════ */
function BookingForm({
  selectedRoom,
  onClose
}: {
  selectedRoom: string | null;
  onClose: () => void;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [submitted, setSubmitted] = useState(false);
  if (!selectedRoom) return null;
  return <AnimatePresence data-id="element-1603">
      <motion.section id="booking" initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} exit={{
      opacity: 0,
      height: 0
    }} className="w-full bg-[#0C1F2C] py-20 sm:py-28 overflow-hidden" ref={ref} data-id="element-1604">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1605">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-1606">
            <div className="flex items-center justify-between mb-8" data-id="element-1607">
              <div data-id="element-1608">
                <div className="flex items-center gap-3 mb-4" data-id="element-1609">
                  <span className="text-[#D4A853]/40 text-xs" data-id="element-1610">◆</span>
                  <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1611" />
                  <span className="text-[#D4A853]/40 text-xs" data-id="element-1612">◆</span>
                </div>
                <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-white mb-1" data-id="element-1613">
                  Check Availability
                </h2>
                <p className="text-slate-400 text-sm" data-id="element-1614">
                  Booking:{' '}
                  <span className="text-[#D4A853] font-medium" data-id="element-1615">
                    {selectedRoom}
                  </span>
                </p>
              </div>
              <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" aria-label="Close booking form" data-id="element-1616">
                <XIcon className="w-4 h-4" data-id="element-1617" />
              </button>
            </div>

            {submitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-12" data-id="element-1618">
                <div className="w-16 h-16 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1619">
                  <CheckIcon className="w-8 h-8 text-teal-400" data-id="element-1620" />
                </div>
                <h3 className="font-playfair text-2xl font-bold text-white mb-2" data-id="element-1621">
                  Request Sent!
                </h3>
                <p className="text-slate-400 text-sm" data-id="element-1622">
                  We'll confirm availability within 24 hours.
                </p>
              </motion.div> : <form onSubmit={e => {
            e.preventDefault();
            setSubmitted(true);
            setTimeout(() => {
              setSubmitted(false);
              onClose();
            }, 3000);
          }} className="space-y-4" data-id="element-1623">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1624">
                  <div data-id="element-1625">
                    <label htmlFor="guest-name" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1626">
                      Full Name
                    </label>
                    <div className="relative" data-id="element-1627">
                      <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1628" />
                      <input id="guest-name" type="text" required placeholder="Your full name" className="w-full pl-10 pr-4 py-3 bg-[#132F3E] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors" data-id="element-1629" />
                    </div>
                  </div>
                  <div data-id="element-1630">
                    <label htmlFor="guest-email" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1631">
                      Email Address
                    </label>
                    <div className="relative" data-id="element-1632">
                      <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1633" />
                      <input id="guest-email" type="email" required placeholder="you@example.com" className="w-full pl-10 pr-4 py-3 bg-[#132F3E] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors" data-id="element-1634" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1635">
                  <div data-id="element-1636">
                    <label htmlFor="check-in" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1637">
                      Check-in Date
                    </label>
                    <div className="relative" data-id="element-1638">
                      <CalendarIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1639" />
                      <input id="check-in" type="date" required className="w-full pl-10 pr-4 py-3 bg-[#132F3E] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors" data-id="element-1640" />
                    </div>
                  </div>
                  <div data-id="element-1641">
                    <label htmlFor="check-out" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1642">
                      Check-out Date
                    </label>
                    <div className="relative" data-id="element-1643">
                      <CalendarIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1644" />
                      <input id="check-out" type="date" required className="w-full pl-10 pr-4 py-3 bg-[#132F3E] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors" data-id="element-1645" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1646">
                  <div data-id="element-1647">
                    <label htmlFor="guests" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1648">
                      Number of Guests
                    </label>
                    <div className="relative" data-id="element-1649">
                      <UsersIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1650" />
                      <select id="guests" className="w-full pl-10 pr-4 py-3 bg-[#132F3E] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#D4A853]/50 transition-colors appearance-none" data-id="element-1651">
                        <option value="1" data-id="element-1652">1 Guest</option>
                        <option value="2" selected data-id="element-1653">
                          2 Guests
                        </option>
                        <option value="3" data-id="element-1654">3 Guests</option>
                      </select>
                    </div>
                  </div>
                  <div data-id="element-1655">
                    <label htmlFor="guest-phone" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1656">
                      Phone Number
                    </label>
                    <div className="relative" data-id="element-1657">
                      <PhoneIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1658" />
                      <input id="guest-phone" type="tel" placeholder="+1 234 567 8900" className="w-full pl-10 pr-4 py-3 bg-[#132F3E] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors" data-id="element-1659" />
                    </div>
                  </div>
                </div>

                <div data-id="element-1660">
                  <label htmlFor="special-requests" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1661">
                    Special Requests (Optional)
                  </label>
                  <div className="relative" data-id="element-1662">
                    <MessageSquareIcon className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" data-id="element-1663" />
                    <textarea id="special-requests" rows={3} placeholder="Any special requirements..." className="w-full pl-10 pr-4 py-3 bg-[#132F3E] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors resize-none" data-id="element-1664" />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4 pt-3" data-id="element-1665">
                  <button type="submit" className="w-full sm:w-auto inline-flex items-center justify-center px-10 py-3.5 bg-teal-600 text-white font-medium rounded-full hover:bg-teal-700 transition-all duration-300 shadow-xl shadow-teal-900/30 text-sm tracking-wide" data-id="element-1666">
                    Send Booking Request
                  </button>
                  <a href="tel:+9779851032316" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-white/20 text-white font-medium rounded-full hover:bg-white/5 transition-all duration-300 text-sm" data-id="element-1667">
                    <PhoneIcon className="w-4 h-4" data-id="element-1668" />
                    Call to Book
                  </a>
                </div>
              </form>}
          </motion.div>
        </div>
      </motion.section>
    </AnimatePresence>;
}
/* ═══════════════════════════════════════════
   MAIN HOTEL PAGE
   ═══════════════════════════════════════════ */
export function HotelPage({
  onNavigate
}: HotelPageProps) {
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const handleSelectRoom = (room: string) => {
    setSelectedRoom(room);
    setTimeout(() => {
      document.getElementById('booking')?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };
  return <div className="w-full" data-id="element-1669">
      <HotelInfo onNavigate={onNavigate} data-id="element-1670" />
      <ImageGallery data-id="element-1671" />
      <HotelAbout data-id="element-1672" />
      <RoomCategories onSelectRoom={handleSelectRoom} data-id="element-1673" />
      <HotelFacilities data-id="element-1674" />
      <BookingForm selectedRoom={selectedRoom} onClose={() => setSelectedRoom(null)} data-id="element-1675" />
    </div>;
}